import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import {
  Wrench, Plus, Search, RefreshCw, Calendar, Clock, User,
  Truck, AlertTriangle, CheckCircle, XCircle, Filter, Eye,
  Edit, FileText, DollarSign, Package, ChevronRight, X,
  ClipboardList, PlayCircle, PauseCircle, StopCircle, Timer } from
'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Input } from '@/shared/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/components/ui/tabs';
import { useFleetAPI, useVehicles } from '../hooks/useFleetData';


const statusColors = {
  scheduled: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  pending_parts: 'bg-purple-100 text-purple-700',
  completed: 'bg-green-100 text-green-700',
  cancelled: 'bg-gray-100 text-gray-700',
  SCHEDULED: 'bg-blue-100 text-blue-700',
  IN_PROGRESS: 'bg-amber-100 text-amber-700',
  PENDING_PARTS: 'bg-purple-100 text-purple-700',
  COMPLETED: 'bg-green-100 text-green-700',
  CANCELLED: 'bg-gray-100 text-gray-700'
};

const priorityColors = {
  low: 'bg-gray-100 text-gray-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700',
  LOW: 'bg-gray-100 text-gray-700',
  MEDIUM: 'bg-blue-100 text-blue-700',
  HIGH: 'bg-orange-100 text-orange-700',
  URGENT: 'bg-red-100 text-red-700'
};

const maintenanceTypes = [
{ value: 'PREVENTIVE', label: 'Preventive', icon: Calendar },
{ value: 'CORRECTIVE', label: 'Corrective', icon: Wrench },
{ value: 'INSPECTION', label: 'Inspection', icon: Eye },
{ value: 'TIRE_SERVICE', label: 'Tire Service', icon: Package },
{ value: 'OIL_CHANGE', label: 'Oil Change', icon: Package },
{ value: 'BRAKE_SERVICE', label: 'Brake Service', icon: AlertTriangle },
{ value: 'ENGINE_REPAIR', label: 'Engine Repair', icon: Wrench },
{ value: 'ELECTRICAL', label: 'Electrical', icon: Wrench },
{ value: 'BODY_WORK', label: 'Body Work', icon: Truck },
{ value: 'OTHER', label: 'Other', icon: ClipboardList }];


const MaintenanceWorkOrdersPage = () => {const { t } = useTranslation("fleet");
  const [activeTab, setActiveTab] = useState('all');
  const [workOrders, setWorkOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const { vehicles } = useVehicles({});
  const { fetchAPI } = useFleetAPI();

  useEffect(() => {
    loadWorkOrders();
  }, []);

  const loadWorkOrders = async () => {
    setLoading(true);
    try {
      const data = await fetchAPI('/maintenance');
      setWorkOrders(data || []);
    } catch (err) {
      console.error('Failed to load work orders:', err);
      // Mock data for demo
      setWorkOrders([
      {
        id: 1,
        maintenance_code: 'WO-260204-001',
        vehicle_id: 1,
        vehicle_code: 'VH-260204-19FF',
        maintenance_type: 'PREVENTIVE',
        title: 'Scheduled Oil Change & Inspection',
        description: '50,000 km preventive maintenance',
        status: 'SCHEDULED',
        priority: 'MEDIUM',
        scheduled_date: '2026-02-10T08:00:00Z',
        estimated_hours: 3,
        labor_cost: 250,
        parts_cost: 450,
        total_cost: 700,
        assigned_technician: 'Carlos Silva',
        created_at: '2026-02-04T10:00:00Z'
      },
      {
        id: 2,
        maintenance_code: 'WO-260204-002',
        vehicle_id: 1,
        vehicle_code: 'VH-260204-19FF',
        maintenance_type: 'CORRECTIVE',
        title: 'Brake Pad Replacement',
        description: 'Front brake pads worn - replacement needed',
        status: 'IN_PROGRESS',
        priority: 'HIGH',
        scheduled_date: '2026-02-04T14:00:00Z',
        started_at: '2026-02-04T14:30:00Z',
        estimated_hours: 2,
        labor_cost: 180,
        parts_cost: 320,
        total_cost: 500,
        assigned_technician: 'João Mendes',
        created_at: '2026-02-03T16:00:00Z'
      },
      {
        id: 3,
        maintenance_code: 'WO-260203-001',
        vehicle_id: 1,
        vehicle_code: 'VH-260204-19FF',
        maintenance_type: 'TIRE_SERVICE',
        title: 'Tire Rotation & Balance',
        description: 'Routine tire maintenance',
        status: 'COMPLETED',
        priority: 'LOW',
        scheduled_date: '2026-02-03T09:00:00Z',
        started_at: '2026-02-03T09:15:00Z',
        completed_at: '2026-02-03T10:30:00Z',
        estimated_hours: 1.5,
        actual_hours: 1.25,
        labor_cost: 100,
        parts_cost: 0,
        total_cost: 100,
        assigned_technician: 'Maria Santos',
        created_at: '2026-02-02T11:00:00Z'
      }]
      );
    } finally {
      setLoading(false);
    }
  };

  // Filter work orders
  const filteredOrders = workOrders.filter((wo) => {
    const matchesSearch =
    wo.maintenance_code?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    wo.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    wo.vehicle_code?.toLowerCase().includes(searchTerm.toLowerCase());

    const status = (wo.status || '').toLowerCase();
    if (activeTab === 'all') return matchesSearch;
    if (activeTab === 'active') return matchesSearch && ['scheduled', 'in_progress', 'pending_parts'].includes(status);
    if (activeTab === 'completed') return matchesSearch && status === 'completed';
    return matchesSearch;
  });

  // Stats
  const stats = {
    total: workOrders.length,
    scheduled: workOrders.filter((w) => (w.status || '').toLowerCase() === 'scheduled').length,
    inProgress: workOrders.filter((w) => (w.status || '').toLowerCase() === 'in_progress').length,
    completed: workOrders.filter((w) => (w.status || '').toLowerCase() === 'completed').length,
    totalCost: workOrders.reduce((sum, w) => sum + (w.total_cost || 0), 0)
  };

  const updateStatus = async (orderId, newStatus) => {
    try {
      await fetchAPI(`/maintenance/${orderId}/status`, {
        method: 'PUT',
        body: JSON.stringify({ status: newStatus })
      });
      loadWorkOrders();
    } catch (err) {
      // Update locally for demo
      setWorkOrders((prev) => prev.map((wo) =>
      wo.id === orderId ? { ...wo, status: newStatus } : wo
      ));
    }
  };

  return (
    <div className="p-6 space-y-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="maintenance-page-title">{t("maintenanceWorkOrders", "Maintenance Work Orders")}

          </h1>
          <p className="text-gray-500">{t("manageVehicleMaintenance", "Manage vehicle maintenance, repairs, and service schedules")}</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search work orders..."
              className="pl-9 w-64 bg-white"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)} />
            
          </div>
          <Button variant="outline" size="sm" onClick={loadWorkOrders}>
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            className="bg-red-500 hover:bg-red-600 text-white"
            size="sm"
            onClick={() => setShowCreateModal(true)}>
            
            <Plus className="w-4 h-4 mr-2" />{t("newWorkOrder", "New Work Order")}

          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t("totalOrders", "Total Orders")}</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                <ClipboardList className="w-5 h-5 text-gray-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t("scheduled", "Scheduled")}</p>
                <p className="text-2xl font-bold text-blue-600">{stats.scheduled}</p>
              </div>
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t("inProgress", "In Progress")}</p>
                <p className="text-2xl font-bold text-amber-600">{stats.inProgress}</p>
              </div>
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <PlayCircle className="w-5 h-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t("completed", "Completed")}</p>
                <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">{t("totalCost", "Total Cost")}</p>
                <p className="text-2xl font-bold text-green-600">{t("r", "R$")}{(stats.totalCost ?? 0).toLocaleString()}</p>
              </div>
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="bg-white p-1 rounded-lg shadow-sm">
          <TabsTrigger value="all" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <ClipboardList className="w-4 h-4 mr-2" />{t("allOrders", "All Orders")}

          </TabsTrigger>
          <TabsTrigger value="active" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <PlayCircle className="w-4 h-4 mr-2" />{t("active", "Active")}

          </TabsTrigger>
          <TabsTrigger value="completed" className="data-[state=active]:bg-red-500 data-[state=active]:text-white">
            <CheckCircle className="w-4 h-4 mr-2" />{t("completed", "Completed")}

          </TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab}>
          <Card className="bg-white border-0 shadow-sm">
            <CardContent className="p-0">
              {loading ?
              <div className="flex items-center justify-center h-32">
                  <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
                </div> :
              filteredOrders.length === 0 ?
              <div className="text-center py-12 text-gray-500">
                  <Wrench className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <h3 className="text-lg font-medium text-gray-900">{t("noWorkOrders", "No Work Orders")}</h3>
                  <p>{t("createANew", "Create a new maintenance work order to get started")}</p>
                  <Button
                  className="mt-4 bg-red-500 hover:bg-red-600 text-white"
                  onClick={() => setShowCreateModal(true)}>
                  
                    <Plus className="w-4 h-4 mr-2" />{t("newWorkOrder", "New Work Order")}

                </Button>
                </div> :

              <div className="divide-y">
                  {filteredOrders.map((order) =>
                <WorkOrderCard
                  key={order.id}
                  order={order}
                  onUpdateStatus={updateStatus}
                  onViewDetails={() => setSelectedOrder(order)} />

                )}
                </div>
              }
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Work Order Modal */}
      {showCreateModal &&
      <CreateWorkOrderModal
        vehicles={vehicles}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => {
          setShowCreateModal(false);
          loadWorkOrders();
        }} />

      }

      {/* Work Order Detail Modal */}
      {selectedOrder &&
      <WorkOrderDetailModal
        order={selectedOrder}
        onClose={() => setSelectedOrder(null)}
        onUpdateStatus={updateStatus} />

      }
    </div>);

};

// Work Order Card Component
const WorkOrderCard = ({ order, onUpdateStatus, onViewDetails }) => {const { t } = useTranslation("fleet");
  const typeConfig = maintenanceTypes.find((t) => t.value === order.maintenance_type) || maintenanceTypes[9];
  const TypeIcon = typeConfig.icon;
  const statusKey = (order.status || '').toUpperCase();
  const priorityKey = (order.priority || '').toUpperCase();

  return (
    <div className="p-4 hover:bg-gray-50 transition-colors">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-4">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${statusKey === 'COMPLETED' ? 'bg-green-100' :
          statusKey === 'IN_PROGRESS' ? 'bg-amber-100' :
          'bg-blue-100'}`
          }>
            <TypeIcon className={`w-6 h-6 ${statusKey === 'COMPLETED' ? 'text-green-600' :
            statusKey === 'IN_PROGRESS' ? 'text-amber-600' :
            'text-blue-600'}`
            } />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="font-bold text-gray-900">{order.maintenance_code}</span>
              <Badge className={statusColors[statusKey] || 'bg-gray-100 text-gray-700'}>
                {statusKey.replace('_', ' ')}
              </Badge>
              <Badge className={priorityColors[priorityKey] || 'bg-gray-100 text-gray-700'}>
                {priorityKey}
              </Badge>
            </div>
            <h3 className="font-medium text-gray-900">{order.title}</h3>
            <p className="text-sm text-gray-500 mt-1">{order.description}</p>
            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
              <span className="flex items-center gap-1">
                <Truck className="w-3 h-3" />
                {order.vehicle_code}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {order.scheduled_date ? new Date(order.scheduled_date).toLocaleDateString() : 'Not scheduled'}
              </span>
              <span className="flex items-center gap-1">
                <Timer className="w-3 h-3" />
                {order.estimated_hours}{t("hEstimated", "h estimated")}
              </span>
              {order.assigned_technician &&
              <span className="flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {order.assigned_technician}
                </span>
              }
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right mr-4">
            <p className="text-lg font-bold text-gray-900">{t("r", "R$")}{(order.total_cost || 0).toLocaleString()}</p>
            <p className="text-xs text-gray-500">{t("totalCost", "Total Cost")}</p>
          </div>
          {statusKey === 'SCHEDULED' &&
          <Button
            size="sm"
            className="bg-amber-500 hover:bg-amber-600 text-white"
            onClick={() => onUpdateStatus(order.id, 'IN_PROGRESS')}>
            
              <PlayCircle className="w-4 h-4 mr-1" />{t("start", "Start")}

          </Button>
          }
          {statusKey === 'IN_PROGRESS' &&
          <Button
            size="sm"
            className="bg-green-500 hover:bg-green-600 text-white"
            onClick={() => onUpdateStatus(order.id, 'COMPLETED')}>
            
              <CheckCircle className="w-4 h-4 mr-1" />{t("complete", "Complete")}

          </Button>
          }
          <Button variant="outline" size="sm" onClick={onViewDetails}>
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>);

};

// Create Work Order Modal
const CreateWorkOrderModal = ({ vehicles, onClose, onSuccess }) => {const { t } = useTranslation("fleet");
  const [formData, setFormData] = useState({
    vehicle_id: '',
    maintenance_type: 'PREVENTIVE',
    title: '',
    description: '',
    priority: 'MEDIUM',
    scheduled_date: '',
    estimated_hours: '',
    assigned_technician: '',
    parts_needed: '',
    labor_cost: '',
    parts_cost: ''
  });
  const [loading, setLoading] = useState(false);
  const { fetchAPI } = useFleetAPI();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await fetchAPI('/maintenance', {
        method: 'POST',
        body: JSON.stringify({
          ...formData,
          vehicle_id: parseInt(formData.vehicle_id),
          estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
          labor_cost: formData.labor_cost ? parseFloat(formData.labor_cost) : 0,
          parts_cost: formData.parts_cost ? parseFloat(formData.parts_cost) : 0
        })
      });
      onSuccess();
    } catch (err) {
      console.error('Failed to create work order:', err);
      // For demo, just close
      onSuccess();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="w-full max-w-2xl m-4 bg-white max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="border-b sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-orange-500" />{t("createWorkOrder", "Create Work Order")}

            </CardTitle>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("vehicle", "Vehicle *")}</label>
                <select
                  className="w-full border rounded-lg p-2.5"
                  value={formData.vehicle_id}
                  onChange={(e) => setFormData((f) => ({ ...f, vehicle_id: e.target.value }))}
                  required>
                  
                  <option value="">{t("selectVehicle", "Select vehicle...")}</option>
                  {vehicles.map((v) =>
                  <option key={v.id} value={v.id}>
                      {v.vehicle_code} - {v.make} {v.model}
                    </option>
                  )}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("type", "Type *")}</label>
                <select
                  className="w-full border rounded-lg p-2.5"
                  value={formData.maintenance_type}
                  onChange={(e) => setFormData((f) => ({ ...f, maintenance_type: e.target.value }))}
                  required>
                  
                  {maintenanceTypes.map((t) =>
                  <option key={t.value} value={t.value}>{t.label}</option>
                  )}
                </select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">{t("title", "Title *")}</label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData((f) => ({ ...f, title: e.target.value }))}
                placeholder="e.g., Scheduled Oil Change"
                required />
              
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">{t("description", "Description")}</label>
              <textarea
                className="w-full border rounded-lg p-2.5 text-sm"
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData((f) => ({ ...f, description: e.target.value }))}
                placeholder="Detailed description of the maintenance work..." />
              
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("priority", "Priority")}</label>
                <select
                  className="w-full border rounded-lg p-2.5"
                  value={formData.priority}
                  onChange={(e) => setFormData((f) => ({ ...f, priority: e.target.value }))}>
                  
                  <option value="LOW">{t("low", "Low")}</option>
                  <option value="MEDIUM">{t("medium", "Medium")}</option>
                  <option value="HIGH">{t("high", "High")}</option>
                  <option value="URGENT">{t("urgent", "Urgent")}</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("scheduledDate", "Scheduled Date")}</label>
                <Input
                  type="datetime-local"
                  value={formData.scheduled_date}
                  onChange={(e) => setFormData((f) => ({ ...f, scheduled_date: e.target.value }))} />
                
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("estHours", "Est. Hours")}</label>
                <Input
                  type="number"
                  step="0.5"
                  value={formData.estimated_hours}
                  onChange={(e) => setFormData((f) => ({ ...f, estimated_hours: e.target.value }))}
                  placeholder="3" />
                
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">{t("assignedTechnician", "Assigned Technician")}</label>
              <Input
                value={formData.assigned_technician}
                onChange={(e) => setFormData((f) => ({ ...f, assigned_technician: e.target.value }))}
                placeholder="Technician name" />
              
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("laborCostR", "Labor Cost (R$)")}</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.labor_cost}
                  onChange={(e) => setFormData((f) => ({ ...f, labor_cost: e.target.value }))}
                  placeholder="0.00" />
                
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">{t("partsCostR", "Parts Cost (R$)")}</label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.parts_cost}
                  onChange={(e) => setFormData((f) => ({ ...f, parts_cost: e.target.value }))}
                  placeholder="0.00" />
                
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={onClose}>{t("cancel", "Cancel")}

              </Button>
              <Button
                type="submit"
                className="bg-red-500 hover:bg-red-600 text-white"
                disabled={loading}>
                
                {loading ?
                <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />{t("creating", "Creating...")}

                </> :

                <>
                    <Plus className="w-4 h-4 mr-2" />{t("createWorkOrder", "Create Work Order")}

                </>
                }
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>);

};

// Work Order Detail Modal
const WorkOrderDetailModal = ({ order, onClose, onUpdateStatus }) => {const { t } = useTranslation("fleet");
  const typeConfig = maintenanceTypes.find((t) => t.value === order.maintenance_type) || maintenanceTypes[9];
  const TypeIcon = typeConfig.icon;
  const statusKey = (order.status || '').toUpperCase();

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <Card className="w-full max-w-2xl m-4 bg-white max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
        <CardHeader className="border-b sticky top-0 bg-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${statusKey === 'COMPLETED' ? 'bg-green-100' :
              statusKey === 'IN_PROGRESS' ? 'bg-amber-100' :
              'bg-blue-100'}`
              }>
                <TypeIcon className={`w-6 h-6 ${statusKey === 'COMPLETED' ? 'text-green-600' :
                statusKey === 'IN_PROGRESS' ? 'text-amber-600' :
                'text-blue-600'}`
                } />
              </div>
              <div>
                <CardTitle>{order.maintenance_code}</CardTitle>
                <p className="text-sm text-gray-500">{typeConfig.label}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose}>
              <X className="w-4 h-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {/* Status and Priority */}
          <div className="flex items-center gap-3">
            <Badge className={`${statusColors[statusKey]} text-sm`}>
              {statusKey.replace('_', ' ')}
            </Badge>
            <Badge className={`${priorityColors[(order.priority || '').toUpperCase()]} text-sm`}>
              {(order.priority || '').toUpperCase()}{t("priority", "Priority")}
            </Badge>
          </div>

          {/* Title and Description */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{order.title}</h3>
            <p className="text-gray-600">{order.description}</p>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-500 mb-1">{t("vehicle", "Vehicle")}</p>
              <p className="font-medium">{order.vehicle_code}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-500 mb-1">{t("technician", "Technician")}</p>
              <p className="font-medium">{order.assigned_technician || 'Not assigned'}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-500 mb-1">{t("scheduled", "Scheduled")}</p>
              <p className="font-medium">
                {order.scheduled_date ? new Date(order.scheduled_date).toLocaleString() : 'Not scheduled'}
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-sm text-gray-500 mb-1">{t("duration", "Duration")}</p>
              <p className="font-medium">{order.estimated_hours}{t("hEstimated", "h estimated")}</p>
            </div>
          </div>

          {/* Costs */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">{t("costBreakdown", "Cost Breakdown")}</h4>
            <div className="bg-gray-50 rounded-lg p-4 space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">{t("labor", "Labor")}</span>
                <span className="font-medium">{t("r", "R$")}{(order.labor_cost || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t("parts", "Parts")}</span>
                <span className="font-medium">{t("r", "R$")}{(order.parts_cost || 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between pt-2 border-t">
                <span className="font-semibold">{t("total", "Total")}</span>
                <span className="font-bold text-lg">{t("r", "R$")}{(order.total_cost || 0).toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            {statusKey === 'SCHEDULED' &&
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={() => {
                onUpdateStatus(order.id, 'IN_PROGRESS');
                onClose();
              }}>
              
                <PlayCircle className="w-4 h-4 mr-2" />{t("startWork", "Start Work")}

            </Button>
            }
            {statusKey === 'IN_PROGRESS' &&
            <>
                <Button
                variant="outline"
                onClick={() => {
                  onUpdateStatus(order.id, 'PENDING_PARTS');
                  onClose();
                }}>
                
                  <PauseCircle className="w-4 h-4 mr-2" />{t("waitingParts", "Waiting Parts")}

              </Button>
                <Button
                className="bg-green-500 hover:bg-green-600 text-white"
                onClick={() => {
                  onUpdateStatus(order.id, 'COMPLETED');
                  onClose();
                }}>
                
                  <CheckCircle className="w-4 h-4 mr-2" />{t("complete", "Complete")}

              </Button>
              </>
            }
            {statusKey === 'PENDING_PARTS' &&
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-white"
              onClick={() => {
                onUpdateStatus(order.id, 'IN_PROGRESS');
                onClose();
              }}>
              
                <PlayCircle className="w-4 h-4 mr-2" />{t("resumeWork", "Resume Work")}

            </Button>
            }
            <Button variant="outline" onClick={onClose}>{t("close", "Close")}

            </Button>
          </div>
        </CardContent>
      </Card>
    </div>);

};

export default MaintenanceWorkOrdersPage;